import BugReportIcon from '@mui/icons-material/BugReport';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';
import BaseModal from '@templates/BaseModal';
import useSnackbar from '@hooks/useSnackbar';
import useT from '@hooks/useT';
import { DebugErrorInfo, DebugInnerException } from '@/types/DebugErrorInfo';

interface DebugErrorModalProps {
  statusCode: number;
  errorMessage: string;
  debugInfo: DebugErrorInfo;
}

function DebugErrorModal({
  statusCode,
  errorMessage,
  debugInfo,
}: Readonly<DebugErrorModalProps>) {
  const { t } = useT();
  const { displaySuccess } = useSnackbar();

  const copyAllAsJson = async () => {
    const fullData = { statusCode, errorMessage, debugInfo };
    await navigator.clipboard.writeText(JSON.stringify(fullData, null, 2));
    displaySuccess(t('debug.copiedToClipboard'));
  };

  const statusColor = statusCode >= 500 ? 'error' : 'warning';

  return (
    <BaseModal
      titre={
        <Stack direction="row" alignItems="center" spacing={2}>
          <BugReportIcon color="error" />
          <Typography variant="h6">{t('debug.errorDetails')}</Typography>
          <Chip label={`HTTP ${statusCode}`} color={statusColor} size="small" />
        </Stack>
      }
    >
      <Stack spacing={2}>
        {/* Copy All Button */}
        <Button
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={() => void copyAllAsJson()}
          fullWidth
        >
          {t('debug.copyAll')}
        </Button>

        {/* Error Message */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>{t('debug.errorMessage')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CodeBlock content={errorMessage} />
          </AccordionDetails>
        </Accordion>

        {/* Stack Trace */}
        <Accordion defaultExpanded={statusCode >= 500}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>{t('debug.stackTrace')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CodeBlock content={debugInfo.stackTrace} />
          </AccordionDetails>
        </Accordion>

        {/* Inner Exceptions */}
        {debugInfo.innerExceptions && debugInfo.innerExceptions.length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>
                {t('debug.innerExceptions')} ({debugInfo.innerExceptions.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {debugInfo.innerExceptions.map((inner, index) => (
                  <InnerExceptionBlock key={index} inner={inner} />
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Request Info */}
        {debugInfo.request && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>{t('debug.requestInfo')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                <Typography variant="body2" fontFamily="monospace">
                  <strong>{debugInfo.request.method}</strong> {debugInfo.request.path}
                  {debugInfo.request.queryString}
                </Typography>
                {debugInfo.request.headers && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Headers:
                    </Typography>
                    <CodeBlock content={JSON.stringify(debugInfo.request.headers, null, 2)} />
                  </Box>
                )}
                {debugInfo.request.body && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Body:
                    </Typography>
                    <CodeBlock content={formatJsonIfPossible(debugInfo.request.body)} />
                  </Box>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}

        {/* User Context */}
        {debugInfo.userContext && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>{t('debug.userContext')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  <strong>User ID:</strong> {debugInfo.userContext.userId ?? 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Client ID:</strong> {debugInfo.userContext.clientId ?? 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Roles:</strong>{' '}
                  {debugInfo.userContext.roles?.join(', ') ?? 'N/A'}
                </Typography>
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}

        {/* SQL Query (Phase 2) */}
        {debugInfo.sqlQuery && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>{t('debug.sqlQuery')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <CodeBlock content={debugInfo.sqlQuery} />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Timestamp */}
        <Typography variant="caption" color="text.secondary" textAlign="right">
          {t('debug.timestamp')}: {new Date(debugInfo.timestamp).toLocaleString()}
        </Typography>
      </Stack>
    </BaseModal>
  );
}

interface CodeBlockProps {
  content: string;
}

function CodeBlock({ content }: Readonly<CodeBlockProps>) {
  return (
    <Box
      sx={{
        backgroundColor: 'grey.100',
        borderRadius: 1,
        p: 1.5,
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxHeight: 300,
        overflow: 'auto',
      }}
    >
      {content}
    </Box>
  );
}

interface InnerExceptionBlockProps {
  inner: DebugInnerException;
}

function InnerExceptionBlock({ inner }: Readonly<InnerExceptionBlockProps>) {
  return (
    <Box
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'error.light',
        borderRadius: 1,
        backgroundColor: 'error.lighter',
      }}
    >
      <Typography variant="subtitle2" color="error.main" fontFamily="monospace">
        {inner.type}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {inner.message}
      </Typography>
      {inner.stackTrace && (
        <Box sx={{ mt: 1 }}>
          <CodeBlock content={inner.stackTrace} />
        </Box>
      )}
    </Box>
  );
}

function formatJsonIfPossible(text: string): string {
  try {
    const parsed: unknown = JSON.parse(text);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return text;
  }
}

export default DebugErrorModal;
